import styled from 'styled-components';
import Badge from './ui/Badge';
import Button from './ui/Button';
import { DIAS_SEMANA, aISO, sumarDias, horaLegible, esHoy } from '../utils/fechas';

const Envoltura = styled.div`
  overflow-x: auto;
  padding-bottom: ${({ theme }) => theme.space.sm};
  -webkit-overflow-scrolling: touch;
`;

const Cuadricula = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(168px, 1fr));
  gap: ${({ theme }) => theme.space.sm};
  min-width: 1200px;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    min-width: 0;
  }
`;

const Dia = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
`;

const CabeceraDia = styled.div`
  text-align: center;
  padding: 0.55rem 0.4rem;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: ${({ theme, $hoy }) => ($hoy ? theme.colors.sage : theme.colors.sand)};
  color: ${({ theme, $hoy }) => ($hoy ? '#ffffff' : theme.colors.textMuted)};

  strong {
    display: block;
    font-size: 0.85rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  span {
    font-size: 0.78rem;
  }
`;

const Bloque = styled.article`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: box-shadow 200ms ease, border-color 200ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.sage};
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const Hora = styled.p`
  font-weight: 700;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.sageDeep};
`;

const Nombre = styled.h3`
  font-size: 1.02rem;
`;

const Instructor = styled.p`
  font-size: 0.82rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SinClases = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: ${({ theme }) => theme.space.md} 0;
`;

function tonoDeCupo(disponible, maximo) {
  if (disponible === 0) return 'peligro';
  if (disponible / maximo <= 0.35) return 'alerta';
  return 'exito';
}

// Calendario semanal lunes-domingo. `clases` viene del API ya filtrado por rango.
function WeekView({ clases, lunes, onReservar, textoBoton = 'Reservar' }) {
  const porDia = clases.reduce((acc, clase) => {
    (acc[clase.fecha] = acc[clase.fecha] || []).push(clase);
    return acc;
  }, {});

  const dias = DIAS_SEMANA.map((nombre, i) => {
    const fecha = sumarDias(lunes, i);
    return { nombre, fecha, iso: aISO(fecha) };
  });

  return (
    <Envoltura>
      <Cuadricula>
        {dias.map(({ nombre, fecha, iso }) => (
          <Dia key={iso}>
            <CabeceraDia $hoy={esHoy(iso)}>
              <strong>{nombre.slice(0, 3)}</strong>
              <span>{fecha.getDate()}</span>
            </CabeceraDia>
            {(porDia[iso] || []).map((clase) => {
              const agotada = clase.cupo_disponible === 0;
              return (
                <Bloque key={clase.id}>
                  <Hora>{horaLegible(clase.hora_inicio)}</Hora>
                  <Nombre>{clase.tipo_clase}</Nombre>
                  <Instructor>
                    {clase.instructor} · {clase.duracion_minutos} min
                  </Instructor>
                  <Badge $tono={tonoDeCupo(clase.cupo_disponible, clase.cupo_maximo)}>
                    {agotada
                      ? 'Clase llena'
                      : `${clase.cupo_disponible}/${clase.cupo_maximo} lugares`}
                  </Badge>
                  {onReservar && (
                    <Button
                      $chico
                      $completo
                      disabled={agotada}
                      onClick={() => onReservar(clase)}
                    >
                      {agotada ? 'Sin cupo' : textoBoton}
                    </Button>
                  )}
                </Bloque>
              );
            })}
            {!(porDia[iso] || []).length && <SinClases>Sin clases</SinClases>}
          </Dia>
        ))}
      </Cuadricula>
    </Envoltura>
  );
}

export default WeekView;
